/**
 * LiveInsightsContext — per-user live meeting insights for the Zoom in-meeting app.
 *
 * Connects to /ws/moderation?meetingId=X&access_token=Y for real-time pushes.
 * Falls back to polling GET /api/meetings/:meetingId/live-insights every 5s when WS is unavailable.
 * Only activates when meeting is live and a bearer token is available.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useMeetingState } from './MeetingStateContext';
import config from '../config';
import api from '../services/api';
import { createInsightsWebSocket, ConnectionState } from '../services/websocket';
import type {
  SharedTeamLiveMeetingViewV1,
  PersonalLiveMeetingViewV1,
  LiveMeetingInsightsDtoV1,
} from '../types/liveInsights';
import { LIVE_MEETING_INSIGHTS_REALTIME_KIND } from '../types/liveInsights';

interface LiveInsightsContextValue {
  sharedTeamView: SharedTeamLiveMeetingViewV1 | null;
  personalView: PersonalLiveMeetingViewV1 | null;
  isLoading: boolean;
  postSharedAction: (signalId: string, action: string) => Promise<void>;
  postPersonalAction: (itemId: string, action: string) => Promise<void>;
}

const LiveInsightsContext = createContext<LiveInsightsContextValue>({
  sharedTeamView: null,
  personalView: null,
  isLoading: false,
  postSharedAction: async () => {},
  postPersonalAction: async () => {},
});

export const useLiveInsights = (): LiveInsightsContextValue => useContext(LiveInsightsContext);

const POLL_INTERVAL_MS = 5000;

export function LiveInsightsProvider({ children }: { children: React.ReactNode }) {
  const { meetingState, insightsToken } = useMeetingState();
  const meetingId = meetingState?.meetingId ?? null;
  const lifecycleStatus = meetingState?.lifecycle?.status ?? null;
  const token = insightsToken || config.dashboardToken;
  const isActive = lifecycleStatus === 'live' && !!meetingId && !!token;

  const [sharedTeamView, setSharedTeamView] = useState<SharedTeamLiveMeetingViewV1 | null>(null);
  const [personalView, setPersonalView] = useState<PersonalLiveMeetingViewV1 | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const wsRef = useRef<ReturnType<typeof createInsightsWebSocket> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const meetingIdRef = useRef<string | null>(null);

  const applyInsights = useCallback((dto: LiveMeetingInsightsDtoV1) => {
    setSharedTeamView(dto.sharedTeamView);
    setPersonalView(dto.personalView);
    setIsLoading(false);
  }, []);

  // Fetch via HTTP (polling fallback)
  const fetchInsights = useCallback(async (mid: string) => {
    try {
      const raw = await api.getLiveInsights(mid) as LiveMeetingInsightsDtoV1 | null;
      if (raw?.sharedTeamView && raw?.personalView) {
        applyInsights(raw);
      }
    } catch {
      // 404/403 — data not available yet or access denied; silently wait for next poll
    }
  }, [applyInsights]);

  // WebSocket + polling lifecycle
  useEffect(() => {
    if (!isActive || !meetingId || !token) {
      // Cleanup
      wsRef.current?.disconnect();
      wsRef.current = null;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setSharedTeamView(null);
      setPersonalView(null);
      setWsConnected(false);
      return;
    }

    // Avoid reconnecting for same meeting
    if (meetingIdRef.current === meetingId && wsRef.current) return;
    meetingIdRef.current = meetingId;
    setIsLoading(true);

    // Initial HTTP fetch
    void fetchInsights(meetingId);

    // Connect WebSocket
    const ws = createInsightsWebSocket(meetingId, token, {
      onStateChange: (state) => {
        const connected = state === ConnectionState.CONNECTED;
        setWsConnected(connected);
      },
      onMessage: (data: unknown) => {
        const msg = data as {
          type?: string;
          envelope?: {
            kind?: string;
            schemaVersion?: number;
            insights?: LiveMeetingInsightsDtoV1;
          };
        };
        if (
          msg.type === LIVE_MEETING_INSIGHTS_REALTIME_KIND &&
          msg.envelope?.kind === LIVE_MEETING_INSIGHTS_REALTIME_KIND &&
          msg.envelope?.insights?.sharedTeamView &&
          msg.envelope?.insights?.personalView
        ) {
          applyInsights(msg.envelope.insights);
        }
      },
    });
    wsRef.current = ws;

    return () => {
      ws.disconnect();
      wsRef.current = null;
      meetingIdRef.current = null;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [isActive, meetingId, token, fetchInsights, applyInsights]);

  // Polling fallback when WS is disconnected
  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (isActive && meetingId && !wsConnected) {
      pollTimerRef.current = setInterval(() => {
        void fetchInsights(meetingId);
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [isActive, meetingId, wsConnected, fetchInsights]);

  const postSharedAction = useCallback(async (signalId: string, action: string) => {
    if (!meetingId) return;
    try {
      await api.postSharedAction(meetingId, { signalId, action });
    } catch (err) {
      console.warn('[LiveInsights] postSharedAction failed', err);
    }
  }, [meetingId]);

  const postPersonalAction = useCallback(async (itemId: string, action: string) => {
    if (!meetingId) return;
    try {
      await api.postPersonalAction(meetingId, { itemId, action });
    } catch (err) {
      console.warn('[LiveInsights] postPersonalAction failed', err);
    }
  }, [meetingId]);

  return (
    <LiveInsightsContext.Provider
      value={{ sharedTeamView, personalView, isLoading, postSharedAction, postPersonalAction }}
    >
      {children}
    </LiveInsightsContext.Provider>
  );
}
